import { baseURL } from '../config';
import { dataService } from '../services/data.services';

/*
 * Large exports are generated by an AWS Batch job instead of inside the export request, which times out at
 * around 3,000 rows. The export API answers with an `id` instead of a `presigned_url`. The export is then handed
 * to a separate tab (public/export-status.html), which polls access-logs/export-status/{id} and downloads the file
 * by itself, so it keeps going when this tab is reloaded, logged out and back in, or closed.
 */

// Same value as LARGE_EXPORT_THRESHOLD in the backend's lib/batch_export.py and lib/batch_export.js.
export const LARGE_EXPORT_THRESHOLD = 3000;

// Served from public/; the page's logic is in public/export-status.js.
const EXPORT_STATUS_PAGE = '/export-status.html';
// Must match EXPORT_KEY in public/export-status.js.
const EXPORT_HANDOFF_KEY = 'arcm-export';

const STATUS_POLL_INTERVAL_MS = 5000;
// How often this tab checks whether the export tab was closed before it finished.
const EXPORT_TAB_CLOSED_CHECK_MS = 1000;
// Consecutive network or server errors tolerated while polling before the export is reported as failed.
const MAX_STATUS_POLL_ERRORS = 3;

const MESSAGES = {
    failedToast: 'Export failed. Please try again.',
    inProgressToast: 'Export is in progress. The file will download automatically when it is ready.',
};

export const isLargeExport = (count) => Number(count) >= LARGE_EXPORT_THRESHOLD;

const openStatusTab = () => window.open(EXPORT_STATUS_PAGE, '_blank');

/**
 * Opens the export tab for a large export. Call it in the click handler before sending the export request:
 * browsers only allow opening a tab during the click, and the request can take longer than that.
 *
 * @param {number} count - rows about to be exported (usually the listing's total count)
 * @returns the tab, or null when the export is small or the browser blocked the tab
 */
export function openExportTab(count) {
    return isLargeExport(count) ? openStatusTab() : null;
}

// Hands the export to the status page in the export tab, which polls and downloads from then on.
// If the tab is closed before it reports back, onTabClosed takes over from this tab.
function handOff(tab, handoff, { setToastmessage, setToastErrorMessage }, onTabClosed) {
    tab.sessionStorage.setItem(EXPORT_HANDOFF_KEY, JSON.stringify(handoff));
    let closedWatch;
    // The page reports back, so this tab can show a toast while it is still the page that started the export.
    const onMessage = (event) => {
        if (event.source !== tab || event.data?.source !== EXPORT_HANDOFF_KEY) {
            return;
        }
        window.removeEventListener('message', onMessage);
        clearInterval(closedWatch);
        if (event.data.outcome === 'success') {
            setToastmessage?.(event.data.message);
        } else {
            setToastErrorMessage?.(MESSAGES.failedToast);
        }
    };
    window.addEventListener('message', onMessage);
    // The page always reports back before it downloads or closes itself, so a tab closed without a report
    // means the export was not delivered yet.
    closedWatch = setInterval(() => {
        if (tab.closed) {
            clearInterval(closedWatch);
            window.removeEventListener('message', onMessage);
            onTabClosed?.();
        }
    }, EXPORT_TAB_CLOSED_CHECK_MS);
}

// Used when there is no export tab (the browser blocked it) or it was closed before the file was ready:
// waits for the file from this tab instead.
function pollExportStatus(id, { setToastmessage, setToastErrorMessage }) {
    let consecutiveErrors = 0;
    const fail = () => setToastErrorMessage?.(MESSAGES.failedToast);
    const check = async () => {
        try {
            const res = await dataService.GetAPI(`access-logs/export-status/${id}`);
            consecutiveErrors = 0;
            const status = res?.data?.data;
            if (status?.presigned_url) {
                // Batch exports are served as downloads (PDFs too), so this does not navigate away from the application.
                window.location.assign(status.presigned_url);
                setToastmessage?.(status.message);
                return;
            }
            setTimeout(check, STATUS_POLL_INTERVAL_MS);
        } catch (error) {
            const statusCode = error?.response?.status;
            // 404: the export record is gone; 500 "Export failed": the batch job reported a failure.
            if (statusCode === 404 || error?.response?.data?.message === 'Export failed') {
                fail();
                return;
            }
            consecutiveErrors += 1;
            if (consecutiveErrors >= MAX_STATUS_POLL_ERRORS) {
                fail();
                return;
            }
            setTimeout(check, STATUS_POLL_INTERVAL_MS);
        }
    };
    check();
}

/**
 * Finishes an export whose request may have been sent to AWS Batch.
 *
 * @param {object} body - the export API response body
 * @param {object} options.exportTab - what openExportTab returned
 * @returns true when this took care of the response: the export was queued (the body has an `id`), or the
 *     export tab was opened and downloads the file. false when the caller should run its usual flow (a small
 *     export, or an error response, after closing any export tab).
 */
export function trackLargeExport(body, { exportTab, setToastmessage, setToastErrorMessage }) {
    const toasts = { setToastmessage, setToastErrorMessage };
    const tab = exportTab && !exportTab.closed ? exportTab : null;
    if (body?.id) {
        setToastmessage?.(MESSAGES.inProgressToast);
        const statusTab = tab || openStatusTab();
        try {
            handOff(statusTab, { id: body.id, api: baseURL }, toasts, () => pollExportStatus(body.id, toasts));
        } catch (error) {
            // No export tab, or it was navigated away from the application.
            pollExportStatus(body.id, toasts);
        }
        return true;
    }
    if (!tab) {
        return false;
    }
    // A couple of provider exports answer with presignedUrl instead of presigned_url.
    const presignedUrl = body?.presigned_url || body?.presignedUrl;
    if (presignedUrl) {
        try {
            handOff(tab, { presigned_url: presignedUrl, message: body.message }, toasts);
            return true;
        } catch (error) {
            // The tab was navigated away from the application: let the caller download it as usual.
            return false;
        }
    }
    tab.close();
    return false;
}
